import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, RefreshCw, CheckCircle, Search, Clock, IndianRupee } from 'lucide-react';
import { BackButton } from '../../components/common';

export const AdminHdfcPendingPage = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState({});
  const [completing, setCompleting] = useState({});
  const [bankReference, setBankReference] = useState({});
  const [adminNotes, setAdminNotes] = useState({});
  const [statusResults, setStatusResults] = useState({});

  const fetchPendingTransactions = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch from both HDFC and UPI Gateway
      const [hdfcRes, upiRes] = await Promise.allSettled([
        adminAPI.getHdfcPendingTransactions(),
        adminAPI.getUpiPendingTransactions()
      ]);
      
      const hdfcTx = hdfcRes.status === 'fulfilled' ? (hdfcRes.value.data.transactions || []) : [];
      const upiTx = upiRes.status === 'fulfilled' ? (upiRes.value.data.transactions || []) : [];
      
      // Mark each transaction with its source
      const allTx = [
        ...hdfcTx.map(tx => ({ ...tx, source: 'hdfc' })),
        ...upiTx.map(tx => ({ ...tx, source: 'upi_gateway' }))
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setTransactions(allTx);
    } catch (error) {
      console.error('Fetch error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/home-new');
      } else if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/home-new');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to fetch transactions');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchPendingTransactions();
  }, [fetchPendingTransactions]);

  const checkStatus = async (tx) => {
    const txId = tx.transaction_id;
    setCheckingStatus(prev => ({ ...prev, [txId]: true }));
    try {
      let response;
      if (tx.source === 'upi_gateway') {
        response = await adminAPI.checkUpiStatus(txId);
        setStatusResults(prev => ({ ...prev, [txId]: response.data }));
      } else {
        response = await adminAPI.checkHdfcStatus(txId);
        setStatusResults(prev => ({ ...prev, [txId]: response.data }));
      }
      toast.success('Status checked - see results below');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check status');
    } finally {
      setCheckingStatus(prev => ({ ...prev, [txId]: false }));
    }
  };

  const manualComplete = async (tx) => {
    if (!window.confirm('Are you sure you want to mark this transaction as completed? Make sure you have verified the payment in the bank statement.')) {
      return;
    }

    const txId = tx.transaction_id;
    setCompleting(prev => ({ ...prev, [txId]: true }));
    try {
      let response;
      if (tx.source === 'upi_gateway') {
        response = await adminAPI.manualCompleteUpi(
          txId,
          bankReference[txId] || '',
          adminNotes[txId] || ''
        );
      } else {
        response = await adminAPI.manualCompleteHdfc(
          txId,
          bankReference[txId] || '',
          adminNotes[txId] || ''
        );
      }
      if (response.data.success) {
        toast.success(`Transaction completed! User balance updated to ${response.data.new_balance} Millis`);
        fetchPendingTransactions();
      } else {
        toast.error(response.data.message || 'Failed to complete transaction');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete transaction');
    } finally {
      setCompleting(prev => ({ ...prev, [txId]: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <BackButton />
        
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">HDFC UPI Pending Transactions</h1>
            <p className="text-gray-500 text-sm mt-1">
              Manage pending HDFC UPI payments that need manual verification
            </p>
          </div>
          <Button onClick={fetchPendingTransactions} disabled={loading} variant="outline">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : transactions.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
              <p className="text-gray-500">No pending HDFC UPI transactions</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {transactions.map((tx) => (
              <Card key={tx.transaction_id} className="overflow-hidden">
                <CardHeader className="bg-gray-50 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending
                      </Badge>
                      <span className="font-mono text-sm text-gray-600">{tx.transaction_id}</span>
                    </div>
                    <span className="text-sm text-gray-500">{formatDate(tx.created_at)}</span>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-500">User</p>
                      <p className="font-medium">{tx.user_name || 'Unknown'}</p>
                      <p className="text-sm text-gray-600">{tx.user_phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Amount</p>
                      <p className="font-medium text-xl flex items-center">
                        <IndianRupee className="w-4 h-4" />
                        {tx.amount_inr?.toLocaleString() || 0}
                      </p>
                      <p className="text-sm text-green-600">{tx.milli_amount} Millis</p>
                    </div>
                  </div>

                  {/* HDFC Status Check Result */}
                  {statusResults[tx.transaction_id] && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
                      <p className="font-medium text-gray-700 mb-2">HDFC API Response:</p>
                      <pre className="bg-white p-2 rounded border text-xs overflow-x-auto">
                        {JSON.stringify(statusResults[tx.transaction_id].hdfc_api_result, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Manual completion form */}
                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Manual Completion</p>
                    <div className="grid md:grid-cols-2 gap-3 mb-3">
                      <Input
                        placeholder="Bank Reference/UTR (optional)"
                        value={bankReference[tx.transaction_id] || ''}
                        onChange={(e) => setBankReference(prev => ({ ...prev, [tx.transaction_id]: e.target.value }))}
                      />
                      <Input
                        placeholder="Admin Notes (optional)"
                        value={adminNotes[tx.transaction_id] || ''}
                        onChange={(e) => setAdminNotes(prev => ({ ...prev, [tx.transaction_id]: e.target.value }))}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => checkHdfcStatus(tx.transaction_id)}
                        disabled={checkingStatus[tx.transaction_id]}
                      >
                        {checkingStatus[tx.transaction_id] ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Search className="w-4 h-4 mr-2" />
                        )}
                        Check HDFC Status
                      </Button>
                      <Button
                        onClick={() => manualComplete(tx.transaction_id)}
                        disabled={completing[tx.transaction_id]}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {completing[tx.transaction_id] ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <CheckCircle className="w-4 h-4 mr-2" />
                        )}
                        Mark as Completed
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminHdfcPendingPage;
